"use client"

import { da } from "date-fns/locale"
import dayjs from "dayjs"
import type { EmployeeUtilization } from "types/employee-utilization"

interface MatrixViewProps {
  utilizations: EmployeeUtilization[]
  startDate: string
  endDate: string
  isLoading: boolean
}

export default function MatrixView({ utilizations, startDate, endDate, isLoading }: MatrixViewProps) {
  // Filter only approved utilizations for matrix view
  const getApprovedUtilizations = () => {
    return utilizations.filter((record) => {
      const recordDate = dayjs(record.date).format("YYYY-MM-DD")
      const isWithinRange = (!startDate || recordDate >= startDate) && (!endDate || recordDate <= endDate)

      return record.status === "approved" && isWithinRange
    })
  }

  // Matrix view logic
  const approvedUtilizations = getApprovedUtilizations()
  const employeeNames = Array.from(new Set(approvedUtilizations.map((u) => u.employee_name))).sort()

  const dates = Array.from(new Set(approvedUtilizations.map((u) => u.date))).sort()

  const matrix: Record<string, Record<string, number>> = {}
  employeeNames.forEach((emp) => {
    matrix[emp] = {}
    dates.forEach((date) => {
      const records = approvedUtilizations.filter((u) => u.employee_name === emp && u.date === date)

      // Sum up all hours for this date
      const totalHours = records.reduce((sum, record) => {
        return sum + (record.total_hours || 0)
      }, 0)

      matrix[emp][date] = totalHours
    })
  })

  const getTotalHours = () => {
    return approvedUtilizations.reduce((total, record) => total + (record.total_hours || 0), 0)
  }

  const getEmployeeTotal = (employeeName: string) => {
    return dates.reduce((total, date) => {
      const dateTotal = matrix[employeeName][date] || 0
      return total + dateTotal
    }, 0)
  }
  const getPercentage = (employeeName: string) => {
    const records = Object.entries(matrix[employeeName]).filter(rec => rec[1] !== 0)
    return Math.round(getEmployeeTotal(employeeName) / records.length  / 8 * 100|| 0).toFixed(2);
    }


  const getDateTotal = (date: string) => {
    return employeeNames.reduce((total, emp) => {
      const dateTotal = matrix[emp][date] || 0
      return total + dateTotal
    }, 0)
  }

  const getHoursColorClass = (hours: number) => {
    if (hours < 6) {
      return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
    } else if (hours >= 6 && hours <= 7) {
      return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
    } else if (hours === 8) {
      return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
    } else {
      return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg border border-gray-100 dark:border-navy-700 overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg border border-gray-100 dark:border-navy-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-navy-700 bg-gray-50 dark:bg-navy-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-50 dark:bg-brand-900/20 rounded-lg flex items-center justify-center">
              <i className="fas fa-table text-brand-600 dark:text-brand-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-navy-700 dark:text-white">Utilization Matrix</h3>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {dayjs(startDate).format("MMM DD")} - {dayjs(endDate).format("MMM DD, YYYY")}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-navy-700/30">
              <th className="sticky left-0 bg-gray-50 dark:bg-navy-700/30 px-4 py-3 text-left font-semibold text-navy-700 dark:text-white border-r border-gray-200 dark:border-navy-600">
                <div className="flex items-center gap-2">
                  <i className="fas fa-user text-xs"></i>
                  <span className="text-sm">Employee</span>
                </div>
              </th>
              {dates.map((date) => (
                <th
                  key={date}
                  className="px-3 py-3 text-center font-semibold text-navy-700 dark:text-white min-w-[80px]"
                >
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-xs">{dayjs(date).format("ddd")}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{dayjs(date).format("DD MMM")}</span>
                  </div>
                </th>
              ))}
              <th className="px-3 py-3 text-center font-semibold text-navy-700 dark:text-white">
                <div className="flex items-center justify-center gap-2">
                  <i className="fas fa-calculator text-xs"></i>
                  <span className="text-sm">Total</span>
                </div>
              </th>
              <th className="px-3 py-3 text-center font-semibold text-navy-700 dark:text-white">
                <div className="flex items-center justify-center gap-2">
                  <i className="fas fa-calculator text-xs"></i>
                  <span className="text-sm">%age</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {employeeNames.length === 0 ? (
              <tr>
                <td colSpan={dates.length + 3} className="text-center py-12">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-navy-700 rounded-full flex items-center justify-center">
                      <i className="fas fa-inbox text-2xl text-gray-400"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-navy-700 dark:text-white mb-1">No Records Found</h3>
                      <p className="text-gray-500 dark:text-gray-400">
                        No approved utilization records found for the selected date range.
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {employeeNames.map((emp, empIndex) => (
                  <tr
                    key={emp}
                    className={`border-b border-gray-100 dark:border-navy-700 hover:bg-gray-50 dark:hover:bg-navy-700/30 transition-colors ${
                      empIndex % 2 === 0 ? "bg-white dark:bg-navy-800" : "bg-gray-50/50 dark:bg-navy-700/20"
                    }`}
                  >
                    <td className="sticky left-0 bg-inherit px-4 py-3 font-medium text-navy-700 dark:text-white border-r border-gray-200 dark:border-navy-600">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-white font-medium text-xs">
                          {emp
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </div>
                        <span className="whitespace-nowrap text-sm">{emp}</span>
                      </div>
                    </td>
                    {dates.map((date) => {
                      const hours = matrix[emp][date]
                      const isWeekend = dayjs(date).day() === 0 || dayjs(date).day() === 6
                      return (
                        <td
                          key={date}
                          className={`px-3 py-3 text-center ${isWeekend ? "bg-gray-100 dark:bg-navy-700/50" : ""}`}
                        >
                          {hours > 0 ? (
                            <span
                              className={`inline-flex items-center justify-center w-10 h-6 rounded-md font-medium text-xs ${getHoursColorClass(
                                hours,
                              )}`}
                            >
                              {hours}
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
                          )}
                        </td>
                      )
                    })}
                    <td className="px-3 py-3 text-center font-semibold text-navy-700 dark:text-white text-sm">
                      {getEmployeeTotal(emp)}
                    </td>
                    <td className="px-3 py-3 text-center font-bold text-navy-700 dark:text-white text-sm">
                        {getPercentage(emp)}
                    </td>
                  </tr>
                ))}
                {/* Totals Row */}
                <tr className="border-t-2 border-gray-200 dark:border-navy-600">
                  <td className="sticky left-0 bg-white dark:bg-navy-800 px-4 py-3 font-semibold text-navy-700 dark:text-white border-r border-gray-200 dark:border-navy-600">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-calculator text-xs"></i>
                      <span className="text-sm">Daily Totals</span>
                    </div>
                  </td>
                  {dates.map((date) => (
                    <td
                      key={date}
                      className="px-3 py-3 text-center font-semibold text-navy-700 dark:text-white text-sm"
                    >
                      {getDateTotal(date)}
                    </td>
                  ))}
                  <td className="px-3 py-3 text-center font-bold text-navy-700 dark:text-white text-base">
                    {getTotalHours()}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
